import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { TableScroll } from '../../components/TableScroll';
import { fetchServices, restartService, fetchCrons, invalidateCache } from './api';
import type { CoolifyAppRow } from './types';
import { useStepUp } from '../auth/useStepUp';

export function ServicesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const stepUp = useStepUp();
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const services = useQuery({
    queryKey: ['services'],
    queryFn: () => fetchServices(),
    refetchInterval: 60_000,
  });

  const crons = useQuery({
    queryKey: ['crons'],
    queryFn: () => fetchCrons(),
  });

  async function handleRestart(svc: CoolifyAppRow) {
    try {
      const token = await stepUp.request({
        action: 'service.restart',
        description: `Coolify-App "${svc.name} (${svc.env})" neu starten`,
        destructive: true,
      });
      await restartService(svc.id, token);
      setFeedback({ type: 'ok', text: `${svc.name} wird neu gestartet.` });
      queryClient.invalidateQueries({ queryKey: ['services'] });
    } catch (err) {
      setFeedback({ type: 'err', text: err instanceof Error ? err.message : 'Fehlgeschlagen' });
    }
  }

  async function handleInvalidate(pattern: string) {
    try {
      const token = await stepUp.request({
        action: 'cache.invalidate',
        description: `Redis-Cache "${pattern}" leeren`,
      });
      const result = await invalidateCache(pattern, token);
      setFeedback({ type: 'ok', text: `${result.deleted} Keys gelöscht (${result.pattern})` });
    } catch (err) {
      setFeedback({ type: 'err', text: err instanceof Error ? err.message : 'Fehlgeschlagen' });
    }
  }

  return (
    <div className="mx-auto max-w-[1200px]">
      {stepUp.modal}
      {feedback && <Toast type={feedback.type} text={feedback.text} />}

      <header className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Service-Management</h1>
        <p className="mt-1 text-xs text-text-tertiary">
          Coolify-Apps, Cache-Invalidation, Cron-Übersicht. Destruktive Aktionen
          erfordern Step-up.
        </p>
      </header>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Coolify-Apps
        </h2>
        {services.isLoading && <SkeletonGrid />}
        {services.error && (
          <div
            className="rounded-md p-3 text-sm"
            style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
          >
            {services.error instanceof Error ? services.error.message : 'Fehler'}
          </div>
        )}
        {services.data && services.data.services.length === 0 && (
          <div
            className="rounded-md border p-4 text-sm text-text-tertiary"
            style={{
              background: darkTheme.colors.bgSurface,
              borderColor: darkTheme.colors.borderSubtle,
            }}
          >
            {services.data.note ?? 'Keine Services gefunden.'}
          </div>
        )}
        {services.data && services.data.services.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {services.data.services.map((s) => (
              <ServiceCard key={s.id} service={s} onRestart={() => handleRestart(s)} />
            ))}
          </div>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Cache-Invalidation
        </h2>
        <div
          className="rounded-md border p-4"
          style={{
            background: darkTheme.colors.bgSurface,
            borderColor: darkTheme.colors.borderSubtle,
          }}
        >
          <p className="mb-3 text-xs text-text-secondary">
            Erlaubte Patterns: <code className="text-accent-light">health:*</code>,{' '}
            <code className="text-accent-light">analytics:*</code>,{' '}
            <code className="text-accent-light">announcement-reach:*</code>
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleInvalidate('health:*')}
              className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay"
            >
              Health-Probes leeren
            </button>
            <button
              type="button"
              onClick={() => handleInvalidate('analytics:*')}
              className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay"
            >
              Analytics-Cache leeren
            </button>
            <button
              type="button"
              onClick={() => handleInvalidate('announcement-reach:*')}
              className="rounded-md border border-border-subtle bg-bg-elevated px-3 py-1.5 text-xs text-text-primary hover:bg-bg-overlay"
            >
              Announcement-Reach leeren
            </button>
          </div>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          Cron-Jobs
        </h2>
        <TableScroll minWidthClass="min-w-[680px]">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-[11px] font-semibold uppercase tracking-wide text-text-tertiary"
                style={{ borderBottom: `1px solid ${darkTheme.colors.borderSubtle}` }}
              >
                <th className="px-4 py-2">Job</th>
                <th className="px-4 py-2">App</th>
                <th className="px-4 py-2">Intervall</th>
                <th className="px-4 py-2">Letzter Run</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Dauer</th>
              </tr>
            </thead>
            <tbody>
              {(crons.data?.crons ?? []).map((c) => (
                <tr
                  key={`${c.name}::${c.app ?? 'global'}`}
                  style={{ borderBottom: `1px solid ${darkTheme.colors.borderSubtle}` }}
                >
                  <td className="px-4 py-3 align-top">
                    <div className="font-mono text-xs text-text-primary">{c.name}</div>
                    {c.description && (
                      <div className="mt-1 max-w-md text-[11px] leading-snug text-text-tertiary">
                        {c.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs">
                    <CronAppBadge app={c.app} />
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-text-secondary">{c.interval}</td>
                  <td className="px-4 py-3 align-top text-xs text-text-tertiary">
                    {c.lastRun ? formatRelative(c.lastRun) : c.note ?? '—'}
                  </td>
                  <td className="px-4 py-3 align-top text-xs">
                    <CronStatusBadge status={c.lastStatus} />
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-text-tertiary tabular">
                    {c.lastDurationMs != null ? `${c.lastDurationMs.toLocaleString('de-DE')} ms` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </section>
    </div>
  );
}

function ServiceCard({
  service,
  onRestart,
}: {
  service: CoolifyAppRow;
  onRestart: () => void;
}): JSX.Element {
  // Container-Status '<state>:<health>' — health 'unknown' heißt nur:
  // kein Docker-HEALTHCHECK im Image, kein Fehlerzustand.
  const [state = 'unknown', health] = service.status.split(':');
  const isOk = state === 'running' && health !== 'unhealthy';
  const statusLabel = health && health !== 'unknown' ? `${state} · ${health}` : state;
  const deploy = service.latestDeploy;
  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">{service.name}</span>
            <EnvBadge env={service.env} />
          </div>
          <div className="text-[10px] tracking-wide text-text-tertiary">
            {service.branch || '—'} · {service.fqdn?.replace(/^https?:\/\//, '') ?? '—'}
          </div>
        </div>
        <span
          className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
          style={{
            background: isOk ? darkTheme.colors.successBg : darkTheme.colors.dangerBg,
            color: isOk ? darkTheme.colors.success : darkTheme.colors.danger,
          }}
        >
          {statusLabel}
        </span>
      </div>

      {deploy && (
        <div className="mb-3 space-y-1">
          <div className="truncate rounded bg-bg-base px-2 py-1 font-mono text-[10px] text-text-secondary">
            {deploy.commit} · {deploy.message?.slice(0, 60) ?? ''}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-text-tertiary">
            <DeployStatusBadge status={deploy.status} />
            {deploy.finishedAt && <span>{formatRelative(deploy.finishedAt)}</span>}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onRestart}
        className="w-full rounded-md px-3 py-1.5 text-xs font-semibold text-white"
        style={{ background: darkTheme.colors.danger }}
      >
        Neu starten
      </button>
    </div>
  );
}

function EnvBadge({ env }: { env: CoolifyAppRow['env'] }): JSX.Element {
  const isProd = env === 'production';
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
      style={{
        background: isProd ? darkTheme.colors.infoBg : darkTheme.colors.bgElevated,
        color: isProd ? darkTheme.colors.info : darkTheme.colors.textSecondary,
      }}
    >
      {env}
    </span>
  );
}

function DeployStatusBadge({ status }: { status: string }): JSX.Element {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    finished: { label: 'Deploy OK', bg: darkTheme.colors.successBg, color: darkTheme.colors.success },
    failed: { label: 'Deploy fehlgeschlagen', bg: darkTheme.colors.dangerBg, color: darkTheme.colors.danger },
    in_progress: { label: 'Deploy läuft', bg: darkTheme.colors.infoBg, color: darkTheme.colors.info },
    queued: { label: 'Deploy wartet', bg: darkTheme.colors.infoBg, color: darkTheme.colors.info },
  };
  const m = map[status] ?? {
    label: status,
    bg: darkTheme.colors.bgElevated,
    color: darkTheme.colors.textSecondary,
  };
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
      style={{ background: m.bg, color: m.color }}
    >
      {m.label}
    </span>
  );
}

function SkeletonGrid(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-32 animate-pulse rounded-md"
          style={{ background: darkTheme.colors.bgSurface }}
        />
      ))}
    </div>
  );
}

function Toast({ type, text }: { type: 'ok' | 'err'; text: string }): JSX.Element {
  return (
    <div
      role="status"
      className="fixed right-6 top-20 z-50 rounded-md px-4 py-2 text-sm shadow-lg"
      style={{
        background: type === 'ok' ? darkTheme.colors.successBg : darkTheme.colors.dangerBg,
        color: type === 'ok' ? darkTheme.colors.success : darkTheme.colors.danger,
      }}
    >
      {text}
    </div>
  );
}

function CronStatusBadge({ status }: { status: string | null }): JSX.Element {
  if (status === null) {
    return <span className="text-text-tertiary">—</span>;
  }
  const map: Record<string, { label: string; bg: string; color: string }> = {
    success: { label: 'OK', bg: darkTheme.colors.successBg, color: darkTheme.colors.success },
    failure: { label: 'Fehler', bg: darkTheme.colors.dangerBg, color: darkTheme.colors.danger },
    running: { label: 'läuft', bg: darkTheme.colors.infoBg, color: darkTheme.colors.info },
  };
  const m = map[status] ?? {
    label: status,
    bg: darkTheme.colors.bgElevated,
    color: darkTheme.colors.textSecondary,
  };
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
      style={{ background: m.bg, color: m.color }}
    >
      {m.label}
    </span>
  );
}

function CronAppBadge({ app }: { app: string | null }): JSX.Element {
  // null = app-agnostischer (globaler) Job; sonst der Tenant des per-app-Laufs.
  if (app === null) {
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[10px] font-medium"
        style={{ background: darkTheme.colors.bgElevated, color: darkTheme.colors.textSecondary }}
        title="App-agnostischer (globaler) Job — läuft genau einmal"
      >
        global
      </span>
    );
  }
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: darkTheme.colors.infoBg, color: darkTheme.colors.info }}
    >
      {app}
    </span>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'gerade eben';
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  if (ms < 86_400_000) return `vor ${Math.floor(ms / 3_600_000)} h`;
  return `vor ${Math.floor(ms / 86_400_000)} Tagen`;
}
